// src/components/TableContainer.jsx
import React from "react";
import {
  useTable,
  usePagination,
  useFilters,
  useSortBy,
  useBlockLayout,
} from "react-table";
import { useSticky } from "react-table-sticky";

import Papa from "papaparse";
import * as XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";

import "../css/tablecomandas.css";
import { DefaultFilterForColumn } from "./Filter";

import { generarPDFCarteraCheques } from "../helpers/pdfCarteraCheques";

/** columnas de acciones / técnicas a excluir */
const SKIP_EXPORT_IDS = new Set([
  "oc",
  "_id",
  "id",
  "edit",
  "edita",
  "editar",
  "borra",
  "borrar",
  "pdf",
  "op",
  "acciones",
  "actions",
]);

function safeDateForFile() {
  return new Date().toISOString().slice(0, 10);
}

function safeDateTimeAR() {
  const d = new Date();
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function toText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);

  if (typeof v === "object") {
    if (v.nombre) return String(v.nombre);
    if (v.name) return String(v.name);
    if (v.label) return String(v.label);
    if (v.value) return String(v.value);
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

function getHeaderText(col) {
  if (typeof col?.exportValue === "string") return col.exportValue;
  if (typeof col?.Header === "string") return col.Header;
  if (typeof col?.id === "string") return col.id;
  if (typeof col?.accessor === "string") return col.accessor;
  return "";
}

function norm(s) {
  return String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function isExportableColumn(col) {
  if (!col) return false;
  if (col.disableExport) return false;

  const id = col.id || (typeof col.accessor === "string" ? col.accessor : "");
  if (id && SKIP_EXPORT_IDS.has(id)) return false;

  const h = norm(getHeaderText(col));
  if (!h) return false;
  if (h === "oc") return false;

  if (
    ["edita", "editar", "edit", "borra", "borrar", "pdf", "op", "acciones", "action"].some((w) =>
      h.includes(w)
    )
  ) return false;

  return true;
}

function isNotesHeader(h) {
  const t = norm(h);
  return t === "notas" || t.includes("nota");
}

function isEconomicHeader(h) {
  const t = norm(h);
  return (
    t.includes("precio") ||
    t.includes("total") ||
    t.includes("compra") ||
    t.includes("venta") ||
    t.includes("flete") ||
    t.includes("comisión") ||
    t.includes("comision") ||
    t.includes("dtv") ||
    t.includes("factura") ||
    t.includes("iva") ||
    t.includes("a (") ||
    t.includes("b (") ||
    t.includes("negro") ||
    t.includes("blanco")
  );
}

/** parser num AR (1.234,56) */
function parseNumberAR(x) {
  if (x === null || x === undefined) return 0;
  if (typeof x === "number") return Number.isFinite(x) ? x : 0;

  let s = String(x).trim();
  if (!s) return 0;

  s = s.replace(/[^\d,.\-]/g, "");
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (lastComma > -1) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    const dots = (s.match(/\./g) || []).length;
    if (dots > 1) s = s.replace(/\./g, "");
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

const fmtIntAR = (n) =>
  new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(Number(n || 0));

const fmtMoneyAR = (n) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

const fmtDateAR = (v) => {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
};

function looksLikeDateString(s) {
  if (typeof s !== "string") return false;
  const t = s.trim();
  if (!t) return false;
  return (
    /\d{4}-\d{2}-\d{2}/.test(t) ||
    /\bGMT[+\-]\d{4}\b/.test(t) ||
    /^[A-Z][a-z]{2}\s[A-Z][a-z]{2}\s\d{1,2}\s\d{4}/.test(t)
  );
}

function formatForExport(value, header) {
  const h = norm(header);

  const headerSaysDate =
    h.includes("fecha") || h.includes("recibido") || h.includes("pago") || h.includes("liq");

  if (value instanceof Date) return fmtDateAR(value);

  if (headerSaysDate) {
    const parsed = value ? new Date(value) : null;
    if (parsed && !isNaN(parsed.getTime())) return fmtDateAR(parsed);
    return toText(value);
  }

  if (looksLikeDateString(value)) {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return fmtDateAR(parsed);
  }

  if (typeof value === "number") {
    if (h.includes("kg") || h.includes("dif") || h.includes("%")) return fmtIntAR(value);
    return fmtMoneyAR(value);
  }

  return toText(value);
}

function chunkArray(arr, chunkSize) {
  const out = [];
  for (let i = 0; i < arr.length; i += chunkSize) out.push(arr.slice(i, i + chunkSize));
  return out;
}

function makeColumnStyles(headers) {
  const styles = {};
  headers.forEach((h, idx) => {
    const t = norm(h);

    if (
      t.includes("kg") ||
      t.includes("precio") ||
      t.includes("total") ||
      t.includes("compra") ||
      t.includes("venta") ||
      t.includes("iva") ||
      t.includes("importe") ||
      t.includes("monto") ||
      t.includes("%") ||
      t === "dif" ||
      t.includes("dif")
    ) {
      styles[idx] = { halign: "right", cellWidth: 20 };
    }

    if (t.includes("remito")) styles[idx] = { ...(styles[idx] || {}), cellWidth: 16, halign: "left" };

    if (t.includes("fecha") || t.includes("recibido") || t.includes("pago") || t.includes("liq")) {
      styles[idx] = { ...(styles[idx] || {}), cellWidth: 20, halign: "center" };
    }

    if (t.includes("proveedor") || t.includes("cliente")) {
      styles[idx] = { ...(styles[idx] || {}), cellWidth: 45, halign: "left" };
    }
    if (t.includes("chofer") || t.includes("transporte")) {
      styles[idx] = { ...(styles[idx] || {}), cellWidth: 28, halign: "left" };
    }
    if (t.includes("lote")) styles[idx] = { ...(styles[idx] || {}), cellWidth: 30, halign: "left" };
  });

  return styles;
}

function shouldSumFooter(header) {
  const t = norm(header);
  if (t.includes("precio")) return false;
  return (
    t.includes("importe") ||
    t.includes("monto") ||
    t.includes("total") ||
    t.includes("compra") ||
    t.includes("venta") ||
    t.includes("kg") ||
    t.includes("iva") ||
    t === "dif" ||
    t.includes("dif")
  );
}

export default function Table({
  columns,
  data,
  title = "INFORME REMITOS",
  filenamePrefix = "Informe",
  pdfProfile = "remitos", // "remitos" | "simple" | "cartera"
  pdfMeta = {},
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    page,
    rows,
    prepareRow,
    state: { pageIndex, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 10,
        pageIndex: 0,
        hiddenColumns: ["oc"],
      },
      defaultColumn: { Filter: DefaultFilterForColumn },
      filterTypes: {
        equals: (rows2, id, filterValue) => rows2.filter((row) => row.values[id] === filterValue),
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useSticky,
    useBlockLayout
  );

  const exportCols = React.useMemo(() => {
    const lastHeaderGroup = headerGroups?.[headerGroups.length - 1];
    const leafCols = lastHeaderGroup?.headers || [];
    return leafCols.filter(isExportableColumn);
  }, [headerGroups]);

  function buildMatrix(all, cols) {
    const src = all ? rows : page;
    const headers = cols.map(getHeaderText);

    const body = src.map((r) => {
      prepareRow(r);
      const cellsById = {};
      r.cells.forEach((c) => (cellsById[c.column.id] = c));

      return cols.map((col) => {
        const cell = cellsById[col.id];
        const header = getHeaderText(col);
        return formatForExport(cell ? cell.value : "", header);
      });
    });

    return { headers, body };
  }

  function drawHeader(doc, metaLineLeft, pageNumber) {
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(20);
    doc.text(String(title || ""), pageW / 2, 10, { align: "center" });

    doc.setDrawColor(210);
    doc.line(6, 12.5, pageW - 6, 12.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(metaLineLeft, 6, 17);

    if (pageNumber) {
      doc.setFontSize(8);
      doc.setTextColor(80);
      doc.text(`Página ${pageNumber}`, pageW - 6, 17, { align: "right" });
    }

    doc.setTextColor(20);
  }

  async function exportPDF(all) {
    const fechaFile = safeDateForFile();
    const fechaPrint = safeDateTimeAR();
    const srcCount = (all ? rows : page).length;
    const modeLabel = all ? "TODO" : "VISTA";

    // ✅ PERFIL CARTERA (usa PDF dedicado, NO autoTable genérico)
    if (pdfProfile === "cartera") {
      const src = all ? rows : page;
      const lista = src.map((r) => {
        prepareRow(r);
        return r.original;
      });

      await generarPDFCarteraCheques(lista, pdfMeta, {});
      return;
    }

    // ✅ PERFIL SIMPLE
    if (pdfProfile === "simple") {
      const { headers, body } = buildMatrix(all, exportCols);
      const isCartera = title?.toLowerCase?.().includes("cartera");

      const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const metaLeft = `Generado: ${fechaPrint}   |   Registros: ${srcCount}   |   ${modeLabel}`;

      const hasFooter = headers.some((h) => shouldSumFooter(h));
      const foot = hasFooter
        ? [
            headers.map((h, i) => {
              if (i === 0) return "TOTALES";
              if (!shouldSumFooter(h)) return "";
              const s = body.reduce((acc, r) => acc + parseNumberAR(r[i]), 0);
              const th = norm(h);
              return th.includes("kg") || th.includes("dif") ? fmtIntAR(s) : fmtMoneyAR(s);
            }),
          ]
        : undefined;

      doc.autoTable({
        head: [headers],
        body,
        foot,
        showFoot: hasFooter ? "lastPage" : undefined,
        theme: "grid",
        styles: {
          fontSize: isCartera ? 6.5 : headers.length >= 12 ? 7 : 8,
          cellPadding: isCartera ? 1 : 1.2,
          valign: "middle",
          overflow: "linebreak",
          textColor: 20,
        },
        headStyles: {
          fillColor: [18, 109, 190],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { top: 22, left: 6, right: 6 },
        columnStyles: isCartera
          ? headers.reduce((acc, _h, i) => {
              acc[i] = { cellWidth: 22 };
              return acc;
            }, {})
          : makeColumnStyles(headers),
        didDrawPage: (d) => drawHeader(doc, metaLeft, d.pageNumber),
      });

      doc.save(`${filenamePrefix}_${fechaFile}_${all ? "todo" : "vista"}.pdf`);
      return;
    }

    // ===== PERFIL REMITOS (secciones) =====
    const keyCols = exportCols.filter((c) => {
      const h = norm(getHeaderText(c));
      return ["remito", "fecha", "proveedor", "cliente"].some((k) => h.includes(k));
    });

    const operCols = exportCols.filter((c) => {
      const h = getHeaderText(c);
      return !isEconomicHeader(h) && !isNotesHeader(h);
    });

    const ecoColsAll = exportCols.filter((c) => isEconomicHeader(getHeaderText(c)));

    const notesCols = exportCols.filter((c) => {
      const h = norm(getHeaderText(c));
      return h.includes("remito") || isNotesHeader(h);
    });

    const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    const commonTableOpts = (headers) => ({
      theme: "grid",
      styles: {
        fontSize: headers.length >= 14 ? 6 : headers.length >= 10 ? 7 : 8,
        cellPadding: 1.2,
        valign: "middle",
        overflow: "linebreak",
        textColor: 20,
      },
      headStyles: {
        fillColor: [18, 109, 190],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      showHead: "everyPage",
      margin: { left: 6, right: 6 },
      columnStyles: makeColumnStyles(headers),
      didDrawPage: (d) => {
        const metaLeft = `Generado: ${fechaPrint}   |   Registros: ${srcCount}   |   ${modeLabel}`;
        drawHeader(doc, metaLeft, d.pageNumber);
      },
    });

    // Sección 1: Operativo
    {
      const { headers, body } = buildMatrix(all, operCols);
      doc.autoTable({
        head: [headers],
        body,
        startY: 24,
        ...commonTableOpts(headers),
      });
    }

    // Sección 2: Económico por bloques
    if (ecoColsAll.length > 0) {
      const ecoOnly = ecoColsAll.filter((c) => !keyCols.some((k) => k.id === c.id));
      const CHUNK = 7;
      const chunks = chunkArray(ecoOnly, CHUNK);

      chunks.forEach((chunk, idx) => {
        doc.addPage();

        const cols = [...keyCols, ...chunk].filter(
          (c, i, arr) => arr.findIndex((x) => x.id === c.id) === i
        );

        const { headers, body } = buildMatrix(all, cols);

        const foot = [
          headers.map((h, i) => {
            if (i === 0) return `TOTALES (${idx + 1}/${chunks.length})`;
            if (!shouldSumFooter(h)) return "";
            const s = body.reduce((acc, r) => acc + parseNumberAR(r[i]), 0);
            return norm(h).includes("kg") || norm(h).includes("dif") ? fmtIntAR(s) : fmtMoneyAR(s);
          }),
        ];

        doc.autoTable({
          head: [headers],
          body,
          foot,
          showFoot: "lastPage",
          startY: 24,
          ...commonTableOpts(headers),
          footStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold" },
        });
      });
    }

    // Sección 3: Notas
    if (notesCols.some((c) => isNotesHeader(getHeaderText(c)))) {
      doc.addPage();
      const { headers, body } = buildMatrix(all, notesCols);
      doc.autoTable({
        head: [headers],
        body,
        startY: 24,
        ...commonTableOpts(headers),
      });
    }

    doc.save(`${filenamePrefix}_${fechaFile}_${all ? "todo" : "vista"}.pdf`);
  }

  function exportXLSX(all) {
    const { headers, body } = buildMatrix(all, exportCols);
    const fecha = safeDateForFile();

    const aoa = [headers, ...body];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!cols"] = headers.map((h, idx) => {
      let maxLen = String(h || "").length;
      for (let r = 0; r < body.length; r++) {
        maxLen = Math.max(maxLen, String(body[r][idx] || "").length);
      }
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Informe");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    downloadBlob(blob, `${filenamePrefix}_${fecha}_${all ? "todo" : "vista"}.xlsx`);
  }

  function exportCSV(all) {
    const { headers, body } = buildMatrix(all, exportCols);
    const fecha = safeDateForFile();

    const csvString = Papa.unparse({ fields: headers, data: body });
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `${filenamePrefix}_${fecha}_${all ? "todo" : "vista"}.csv`);
  }

  return (
    <>
      <div className="container mb-3">
        <div className="d-flex">
          <button className="btn btn-primary btn-sm flex-fill mx-1" style={{ fontSize: "1rem" }} onClick={() => exportXLSX(true)}>
            Exportar TODO Excel
          </button>

          <button className="btn btn-info btn-sm flex-fill mx-1" style={{ fontSize: "1rem" }} onClick={() => exportXLSX(false)}>
            Exportar Vista Excel
          </button>

          <button className="btn btn-success btn-sm flex-fill mx-1" style={{ fontSize: "1rem" }} onClick={() => exportPDF(true)}>
            Exportar TODO PDF
          </button>

          <button className="btn btn-warning btn-sm flex-fill mx-1" style={{ fontSize: "1rem" }} onClick={() => exportPDF(false)}>
            Exportar Vista PDF
          </button>
        </div>

        <div className="d-flex mt-2">
          <button className="btn btn-outline-secondary btn-sm flex-fill mx-1" onClick={() => exportCSV(true)}>
            Exportar TODO CSV
          </button>
          <button className="btn btn-outline-secondary btn-sm flex-fill mx-1" onClick={() => exportCSV(false)}>
            Exportar Vista CSV
          </button>
        </div>
      </div>

      <table className="table sticky table-striped table-bordered" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th key={column.id} {...column.getHeaderProps(column.getSortByToggleProps())} className={column.className}>
                  {column.render("Header")}
                  <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr key={row.id} {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td key={cell.column.id} {...cell.getCellProps({ className: cell.column.className })}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          {footerGroups.map((group) => (
            <tr key={group.id} {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <td key={column.id} {...column.getFooterProps()}>
                  {column.render("Footer")}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>

      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>{" "}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>{" "}
        <button onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>{" "}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>{" "}
        <span>
          Página {pageIndex + 1} de {pageOptions.length} {" - Ir a:"}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => gotoPage(e.target.value ? Number(e.target.value) - 1 : 0)}
            style={{ width: "60px", marginLeft: "4px" }}
          />
        </span>{" "}
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ marginLeft: "8px" }}>
          {[10, 50, 100, 150, 200, 250].map((size) => (
            <option key={size} value={size}>
              Mostrar {size}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}